import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../../features/auth/contexts/useAuth';
import { NavItem } from './types';
import {
  Route,
  Home,
  CarFront,
  LayoutDashboard,
  Calendar,
} from 'lucide-react';

export const useNavigation = () => {
  const { user, isAuthenticated, redirectToLogin } = useAuth();
  const location = useLocation();
  const [isNavVisible, setIsNavVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Handle scroll events to update navbar appearance and visibility
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        // Scrolling down
        setIsNavVisible(false);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setIsNavVisible(true);
      }
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (userMenuOpen && !target.closest('[data-user-menu]')) {
        setUserMenuOpen(false);
      }
      if (mobileMenuOpen && !target.closest('[data-mobile-menu]')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen, mobileMenuOpen]);

  const handleSignIn = () => {
    redirectToLogin('', location.pathname);
  };

  // Get navigation items based on user type
  const getMainNavItems = (): NavItem[] => {
    if (!isAuthenticated) {
      return [
        { label: 'Home', path: '/', icon: Home },
        { label: 'Safaris', path: '/safaris-search', icon: Route },
        { label: 'Trips', path: '/transport-search', icon: CarFront }
      ];
    }

    // Add user-specific management pages
    const userItems: NavItem[] = [];

    // Home page for all users
    userItems.push({ label: 'Home', path: '/', icon: Home });

    // Common pages for all authenticated users
    userItems.push({ label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard });
    userItems.push({ label: 'Bookings', path: '/bookings', icon: Calendar });

    // Operator-specific pages
    if (user?.type === 'operator') {
      if (user?.operator_type === 'transporter') {
        userItems.push({ label: 'Trips', path: '/trips', icon: CarFront });
        userItems.push({ label: 'Fleet', path: '/fleet', icon: CarFront });
      }
    }

    // Add public pages for non-transporter users
    if (user?.type !== 'operator' || user?.operator_type === 'tour_operator') {
      userItems.push({ label: 'Safaris', path: '/safaris', icon: Route });
      userItems.push({ label: 'Trips', path: '/trips', icon: CarFront });
    }

    return userItems;
  };

  // Check if we're on a dashboard page
  const isDashboardPage = location.pathname === '/dashboard' || 
                         location.pathname === '/bookings' || 
                         location.pathname === '/trips' || 
                         location.pathname === '/fleet' || 
                         location.pathname === '/account' ||
                         location.pathname === '/notifications' ||
                         location.pathname === '/payments' ||
                         location.pathname === '/help' ||
                         location.pathname === '/safaris' ||
                         location.pathname === '/fleet/drivers' ||
                         location.pathname === '/reviews' ||
                         location.pathname === '/verification-required';

  return {
    isNavVisible,
    mobileMenuOpen,
    setMobileMenuOpen,
    userMenuOpen,
    setUserMenuOpen,
    handleSignIn,
    getMainNavItems,
    isDashboardPage,
    user,
    isAuthenticated,
  };
}; 